import type { ObjectMetadata } from '@olytic/foundation';

import { formatMoney, formatNumber } from '../../objects/format';
import { OBJECT_DEFS } from '../../objects/objectDefs';
import { asDate, asLabel, column, createdByField, lastModifiedByField, listColumnsOf, lookup, type Row, related, schemaRecord, show, text } from './kit';

const def = OBJECT_DEFS.products;
const quantity = show((v) => formatNumber(v));
const share = show((v) => `${formatNumber(v)}%`);

/**
 * A product's record page tabs, by key, and the readiness `fix` each one
 * answers (ops.product_readiness: details, pricing, billing, bundle,
 * delivery). The Details checklist links each missing item to its tab.
 * Details and Related come from ObjectRecordPage; the rest are
 * products/ProductPage.tsx, added in ObjectRecordView.
 */
export const PRODUCT_TABS = {
  setup: 'Details',
  catalog: 'Catalog',
  pricing: 'Pricing',
  structure: 'Structure',
  billing: 'Billing',
  operations: 'Operations',
  sales: 'Sales',
} as const;
export type ProductTab = keyof typeof PRODUCT_TABS;
export const TAB_FOR_FIX: Readonly<Record<string, ProductTab>> = {
  details: 'setup',
  pricing: 'pricing',
  billing: 'billing',
  bundle: 'structure',
  delivery: 'operations',
};
/** Readiness items fixed by one of the product's own fields (on the Setup tab), by the field's label. */
export const FIELD_FOR_ITEM: Readonly<Record<string, string>> = {
  description: 'Description',
  billing_policy: 'Billing policy',
  qbo_item: 'QuickBooks item',
  estimated_hours: 'Estimated hours',
  size: 'Size',
};

/**
 * Its fields come from the database (the server's SCHEMA_OBJECTS), shown by
 * their type, the Billing Policy linked to its record; any column not placed
 * below appears in Other Fields (MOTHER-303): today Sort Order, which the page
 * never placed. Active stays off, as before: it only follows Status (0029,
 * product_sync_active). The Group links to its group, as before. Seniority and
 * Risk Class keep their labels: their CHECKs allow no value, so the catalog
 * reads them as plain text.
 */
const field = schemaRecord('products', { hidden: ['active'] });

const details = [
  field('name', 'Name'),
  field('code', 'Code'),
  field('group_name', 'Group', { link: lookup('product-groups', 'group_id') }),
  field('family', 'Family'),
  field('type', 'Type'),
  field('structure', 'Structure'),
  field('is_component', 'Component', {
    help: 'Sold only as an option inside a bundle: never billed, scheduled or sent to QuickBooks on its own.',
  }),
  field('status', 'Status'),
  field('billing_policy_name', 'Billing Policy'),
  field('qbo_item_id', 'QuickBooks Item'),
  field('size', 'Size'),
  field('description', 'Description', { wide: true }),
  // Hours per unit sold (a month of a monthly price); a Price Grid's cells carry their own.
  {
    key: 'estimated_hours',
    label: 'Estimated Hours',
    render: (row: Row) => (row.estimated_hours === null || row.estimated_hours === undefined ? text(null) : `${formatNumber(row.estimated_hours)} h`),
  },
  field('seniority', 'Seniority', { format: asLabel, help: 'Who does the work: junior, mid or senior. Only services have one, and every service needs one.' }),
  field('risk_class', 'Risk Class', {
    format: asLabel,
    help: 'How far the estimate is likely to run over: low (x1.15), medium (x1.3) or high (x1.6, many dependencies on other systems).',
  }),
  field('created_at', 'Created'),
  field('updated_at', 'Last Modified'),
  createdByField(),
  lastModifiedByField(),
];

/** A product: one SKU in the catalog, configured end to end before it sells. Rationale: products.md. */
export const products: ObjectMetadata<Row> = {
  key: def.key,
  singular: 'Product',
  plural: def.label,
  icon: def.icon,
  listColumns: listColumnsOf(def, details),
  details,
  sections: [
    { key: 'identity', fields: ['name', 'code', 'group_name', 'family', 'type', 'structure', 'is_component', 'status'] },
    { key: 'commercial', label: 'Billing', fields: ['billing_policy_name', 'qbo_item_id'] },
    { key: 'description', label: 'Description', fields: ['description'] },
    { key: 'internal', label: 'Internal Estimate', fields: ['estimated_hours', 'size', 'seniority', 'risk_class'] },
    { key: 'system', label: 'System', collapsed: true, fields: ['created_at', 'created_by_name', 'updated_at', 'last_modified_by_name'] },
  ],
  related: [
    // Pricing
    related('prices', 'Prices', 'prices', [
      column('name', 'Price', { primary: true }),
      column('selling_model', 'Selling Model'),
      column('unit_price', 'Amount', { format: show(formatMoney), align: 'right' }),
      column('price_grid_name', 'Price Grid', { link: lookup('price-grids', 'price_grid_id') }),
      column('is_default', 'Default'),
    ]),
    // Billing
    related('billing-policy', 'Billing Policy', 'billing-policies', [
      column('name', 'Billing Policy', { primary: true }),
      column('description', 'Description'),
    ]),
    related('billing-treatments', 'Billing Treatments', 'billing-treatments', [
      column('name', 'Treatment', { primary: true }),
      column('method', 'Method', { format: asLabel }),
      column('billing_frequency', 'Frequency', { format: asLabel }),
      column('is_default', 'Default'),
    ]),
    // Bundle
    related('options', 'Components', 'product-options', [
      column('name', 'Product', { primary: true }),
      column('option_group', 'Group'),
      column('required', 'Required'),
      column('default_quantity', 'Default Qty', { format: quantity, align: 'right' }),
      column('included', 'Included'),
    ]),
    related('requires', 'Requires', 'products', [
      column('name', 'Product', { primary: true }),
      column('status', 'Status', { format: asLabel }),
    ]),
    related('bundles', 'Part of Bundles', 'product-options', [
      column('name', 'Bundle', { primary: true }),
      column('required', 'Required'),
      column('included', 'Included'),
    ]),
    related('features', 'Includes', undefined, [
      column('name', 'Feature', { primary: true }),
      column('value', 'Included'),
    ]),
    // Delivery
    related('role-shares', 'Role Shares', 'product-resource-roles', [
      column('name', 'Role', { primary: true }),
      column('share_pct', 'Share of Hours', { format: share, align: 'right' }),
    ]),
    // Guidelines
    related('guidelines', 'Guidelines', 'guidelines', [
      column('name', 'Guideline', { primary: true }),
      column('topic', 'Topic', { format: asLabel }),
      column('enforcement', 'Enforcement', { format: asLabel }),
      column('weight', 'Weight', { align: 'right' }),
    ]),
    // Usage (quote lines have no page of their own: the quote and account link)
    related('quote-lines', 'Quote Lines', undefined, [
      column('name', 'Line', { primary: true }),
      column('quote_name', 'Quote', { link: lookup('quotes', 'quote_id') }),
      column('account_name', 'Account', { link: lookup('accounts', 'account_id') }),
      column('quantity', 'Quantity', { format: quantity, align: 'right' }),
    ]),
    related('assets', 'Assets', 'assets', [
      column('name', 'Asset', { primary: true }),
      column('account_name', 'Account', { link: lookup('accounts', 'account_id') }),
      column('start_date', 'Start', { format: asDate }),
      column('end_date', 'End', { format: asDate }),
    ]),
  ],
  titleOf: (row) => text([row.code, row.name].filter(Boolean).join(' · ')),
  searchKeys: ['code', 'name', 'group_name'],
};
